import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { deletePromoCode, getAllPromoCode } from '../utils/api/promoCode';
import { convertToIndianTimeZone } from '../utils/utils';

import AddPromoCodeModal from './child/AddPromoCodeModal';
import UpdatePromoCode from './child/updatePromoCode';
import ViewPromoCodeModal from './child/viewPromoCodeModal';

function PromoCodeLayer() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get('page') || '', 10) || 1;
  const limit = parseInt(searchParams.get('limit') || '', 10) || 10;
  const isActive = searchParams.get('isActive') || 'all';

  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPromoId, setSelectedPromoId] = useState(null);

  const updateURLParams = (newParams) => {
    setSearchParams({
      page: (newParams.page ?? page).toString(),
      limit: (newParams.limit ?? limit).toString(),
      isActive: newParams.isActive ?? isActive,
    });
  };

  const fmtNumber = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return String(val ?? '');
    return Number.isInteger(num) ? String(num) : String(+num.toFixed(2)).replace(/\.00$/, '');
  };

  const renderPromoType = (item) =>
    item?.type === 'percentage' ? 'Percentage' : 'Flat';

  const renderDiscount = (item) => {
    const v = fmtNumber(item?.value ?? item?.discountPercent ?? 0);
    return item?.type === 'percentage' ? `${v}%` : `₹${v} off`;
  };

  const fetchPromoCodes = async () => {
    setLoading(true);
    try {
      const payload = {
        page,
        limit,
        isActive: isActive === 'all' ? undefined : isActive === 'true',
      };
      const res = await getAllPromoCode(payload);
      const { promoCodes: list = [], totalPages: tp = 0 } = res?.data || {};
      setPromoCodes(Array.isArray(list) ? list : []);
      setTotalPages(Number(tp) || 0);
    } catch (e) {
      console.error('Error fetching promo codes:', e);
      toast.error(e?.message || 'Failed to load promo codes');
      setPromoCodes([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromoCodes();
  }, [page, limit, isActive]);

  const handleModalClose = () => setShowAddModal(false);

  const handleDelete = async (id) => {
    try {
      const res = await deletePromoCode(id);
      console.log('Delete response:', res);
      toast.success(res?.message || 'Promo code deleted successfully');
      fetchPromoCodes();
    } catch (error) {
      toast.error(error?.message || 'Error deleting promo code');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="card h-100 p-0 radius-12"
    >
      <div className="card-header border-bottom bg-base py-16 px-24">
        <div className="d-flex justify-content-between align-items-center flex-wrap w-100" style={{ gap: '12px' }}>
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="active-filter" className="mb-0">Status:</label>
            <div className="position-relative" style={{ width: '200px' }}>
              <Icon
                icon="lucide:filter"
                width="18"
                height="18"
                style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }}
              />
              <select
                id="active-filter"
                name="isActive"
                className="form-control"
                value={isActive}
                onChange={(e) => updateURLParams({ isActive: e.target.value, page: 1 })}
                style={{ paddingLeft: '36px', paddingRight: '30px', height: '45px', borderRadius: '4px' }}
              >
                <option value="all">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
              <Icon
                icon="lucide:chevron-down"
                width="18"
                height="18"
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }}
              />
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add New Promo Code
          </button>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th className="text-center">Promo Code</th>
                <th className="text-center">Promo Type</th>
                <th className="text-center">Discount</th>
                <th className="text-center">Is Active</th>
                <th className="text-center">Created At</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`shimmer-${idx}`}>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <td key={i}><div className="shimmer shimmer-text" /></td>
                    ))}
                  </tr>
                ))
              ) : promoCodes.length > 0 ? (
                promoCodes.map((item) => {
                  const rowId = item?.id || item?._id || `${item.code}-${item.createdAt}`;
                  return (
                    <tr key={rowId}>
                      <td className="text-center">{item?.code}</td>
                      <td className="text-center">{renderPromoType(item)}</td>
                      <td className="text-center">{renderDiscount(item)}</td>
                      <td className="text-center">
                        <span className={`badge ${item?.isActive ? 'bg-success' : 'bg-danger'}`}>
                          {item?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-center">{convertToIndianTimeZone(item?.createdAt)}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-10 justify-content-center">
                          <button
                            onClick={() => {
                              setSelectedPromoId(item?.id || item?._id);
                              setShowViewModal(true);
                            }}
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="majesticons:eye-line" className="icon text-xl" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedPromoId(item?.id || item?._id);
                              setShowEditModal(true);
                            }}
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                          <button
                            onClick={() => handleDelete(item?.id || item?._id)}
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="ic:outline-block" className="menu-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6}>
                    <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '40vh' }}>
                      <Icon icon="icon-park-outline:file-search" className="text-3xl mb-3 text-muted" />
                      <p className="text-secondary-light text-md mb-0">No promo codes found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-10">
          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => updateURLParams({ page: Math.max(1, page - 1) })}
              disabled={page === 1}
            >
              Prev
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => updateURLParams({ page: page + 1 })}
              disabled={page === totalPages || totalPages === 0}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ViewPromoCodeModal
        id={selectedPromoId}
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
      />
      <UpdatePromoCode
        id={selectedPromoId}
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          fetchPromoCodes();
        }}
      />
      <AddPromoCodeModal
        show={showAddModal}
        onClose={() => {
          handleModalClose();
          fetchPromoCodes();
        }}
      />
    </motion.div>
  );
}

export default PromoCodeLayer;
